<template>
  <div class="blocked-candidates-page" dir="rtl">
    <h1 class="page-title">מועמדים חסומים</h1>
    <p class="page-subtitle">
      מועמד/ת חסום/ה: אין להגיש למעסיקים ואין ליצור קשר. קורות החיים והתקצירים נמחקים, והשיחות, ההגשות וההיסטוריה נשמרים.
      הגשה דרך סטארגייט של חסום/ה נדחית אוטומטית. רק מנהל/ת יכול/ה להסיר חסימה - והסרה לא משחזרת קו"ח שנמחקו.
    </p>

    <label class="include-released">
      <input type="checkbox" v-model="include_released" data-test="include-released" @change="loadCandidates" />
      הצג גם חסימות שהוסרו
    </label>

    <!-- Loading -->
    <div v-if="is_loading" class="loading-wrap">
      <div class="loading"></div>
    </div>

    <template v-else>
      <div v-if="candidates.length" class="table-wrap">
        <table class="blocked-table">
          <thead>
            <tr>
              <th>שם</th>
              <th>טלפון</th>
              <th>אימייל</th>
              <th>סיבה</th>
              <th>נחסם בתאריך</th>
              <th>נחסם ע"י</th>
              <th>קו"ח</th>
              <th>צ'אטבוט</th>
              <th class="col-num">הגשות שנחסמו</th>
              <th>הגשה אחרונה</th>
              <th class="col-action"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="candidate in candidates" :key="candidate.id" :class="{ 'is-released': candidate.released_at }">
              <td>{{ candidate.full_name || '-' }}</td>
              <td class="ltr-cell">{{ candidate.phone_number || '-' }}</td>
              <td class="ltr-cell">{{ candidate.email || '-' }}</td>
              <td class="col-reason">{{ candidate.reason || '-' }}</td>
              <td>{{ formatDate(candidate.created_at) }}</td>
              <td>{{ candidate.blocked_by_name || '-' }}</td>
              <td data-test="cv-status">
                <template v-if="candidate.legacy">נמחק (חסימה ישנה)</template>
                <template v-else>{{ statusLabel(candidate.cv_erasure_status) }}</template>
              </td>
              <td data-test="contact-status">
                <template v-if="candidate.legacy">-</template>
                <template v-else>{{ statusLabel(candidate.contact_suppression_status) }}</template>
                <div v-if="hasFailedCleanup(candidate) && candidate.cleanup_error" class="cleanup-error" data-test="cleanup-error">{{ candidate.cleanup_error }}</div>
              </td>
              <td class="col-num">{{ candidate.hits_count || 0 }}</td>
              <td>{{ candidate.last_hit_at ? formatDate(candidate.last_hit_at) : '-' }}</td>
              <td class="col-action">
                <button
                  class="details-btn"
                  :class="{ 'is-muted': !hasDetails(candidate) }"
                  :disabled="!hasDetails(candidate)"
                  @click="openDetails(candidate)"
                >{{ hasDetails(candidate) ? 'פירוט' : 'אין פירוט' }}</button>
                <button
                  v-if="can_unblock && !candidate.legacy && !hasDetails(candidate)"
                  class="details-btn"
                  :disabled="busy_id === candidate.id"
                  data-test="create-dossier"
                  @click="createDossier(candidate)"
                >צור פירוט</button>
                <span v-if="candidate.released_at" class="released-note" data-test="released-note">
                  הוסרה ב-{{ formatDate(candidate.released_at) }}<template v-if="candidate.released_by_name"> ע"י {{ candidate.released_by_name }}</template>
                </span>
                <template v-else>
                  <button
                    v-if="can_unblock && hasFailedCleanup(candidate)"
                    class="details-btn"
                    :disabled="busy_id === candidate.id"
                    data-test="retry-cleanup"
                    @click="retryCleanup(candidate)"
                  >נסה שוב</button>
                  <button
                    v-if="can_unblock"
                    class="unblock-btn"
                    :disabled="busy_id === candidate.id"
                    data-test="release"
                    @click="releaseBlock(candidate)"
                  >הסרת חסימה</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-else class="no-results">אין מועמדים חסומים</p>
    </template>

    <!-- Display-only details dialog. Kept inside this view (not the modals store)
         because no generic content modal exists in Modal.vue's registry — see the
         note on openDetails(). -->
    <div v-if="details_candidate" class="details-overlay" @click.self="closeDetails">
      <div class="details-dialog" dir="rtl" role="dialog" aria-modal="true">
        <div class="details-header">
          <p class="details-title">פירוט חסימה - {{ details_candidate.full_name || 'מועמד' }}</p>
          <button class="details-close-x" title="סגור" @click="closeDetails">×</button>
        </div>
        <div class="details-body" dir="rtl">{{ details_candidate.details }}</div>
        <p v-if="details_candidate.details_source === 'facts_only'" class="details-note">
          נוצר ללא AI - עובדות בלבד
        </p>
        <div class="details-footer">
          <button class="details-close-btn" @click="closeDetails">סגור</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cleanupStatusLabel, askReason, releaseErrorText } from '@/custom/helpers/applicant_block.js'

export default {
  name: 'blocked-candidates-page',
  data () {
    return {
      candidates: [],
      // From GET /blocked_candidates { can_unblock, rows }: the current admin is a manager.
      can_unblock: false,
      include_released: false,
      is_loading: false,
      // Row id with a release / retry / dossier call in flight (blocks double clicks).
      busy_id: null,
      // The row whose block dossier is open; null = dialog closed.
      details_candidate: null
    }
  },
  mounted () {
    this.loadCandidates()
  },
  methods: {
    loadCandidates () {
      this.is_loading = true
      this.$network_utils
        .getBlockedCandidates({ include_released: this.include_released })
        .then(res => {
          // { can_unblock, rows }; tolerate a bare array from an older server.
          const rows = Array.isArray(res) ? res : ((res && res.rows) || [])
          this.can_unblock = !Array.isArray(res) && !!(res && res.can_unblock)
          this.candidates = rows
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בטעינת רשימת המועמדים החסומים', type: 'error' })
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    statusLabel (status) {
      return cleanupStatusLabel(status)
    },
    hasFailedCleanup (candidate) {
      return !candidate.legacy && (candidate.cv_erasure_status === 'failed' || candidate.contact_suppression_status === 'failed')
    },
    hasDetails (candidate) {
      return !!(candidate && candidate.details && String(candidate.details).trim())
    },
    // Display-only, so it is NOT routed through the modals store: every component
    // registered in Modal.vue is feature-specific, and ModalConfirmation renders a
    // bare {{ message }} with כן/לא buttons — no pre-wrap, no muted note, no 'סגור'.
    // Giving it those would mean editing the shared confirm dialog used app-wide.
    openDetails (candidate) {
      if (!this.hasDetails(candidate)) {
        return
      }
      this.details_candidate = candidate
    },
    closeDetails () {
      this.details_candidate = null
    },
    // Reason is required and is stored in the audit trail; the server enforces manager-only.
    releaseBlock (candidate) {
      if (!candidate || this.busy_id) {
        return
      }
      const reason = askReason('סיבת הסרת החסימה (חובה):', text => this.$utils.emit('show_toast', { text, type: 'error' }))
      if (!reason) {
        return
      }
      this.busy_id = candidate.id
      this.$network_utils
        .releaseBlockedCandidate({ id: candidate.id, reason })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'החסימה הוסרה' })
          this.loadCandidates()
        })
        .catch(error => {
          this.$utils.emit('show_toast', { text: releaseErrorText(error), type: 'error' })
        })
        .finally(() => {
          this.busy_id = null
        })
    },
    retryCleanup (candidate) {
      if (!candidate || this.busy_id) {
        return
      }
      this.busy_id = candidate.id
      this.$network_utils
        .retryBlockedCleanup({ id: candidate.id })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'ניסיון חוזר נשלח - הסטטוס יתעדכן בקרוב' })
          this.loadCandidates()
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'הניסיון החוזר נכשל', type: 'error' })
        })
        .finally(() => {
          this.busy_id = null
        })
    },
    // Dossier is no longer generated at block time; a manager can build it on demand.
    createDossier (candidate) {
      if (!candidate || this.busy_id) {
        return
      }
      this.busy_id = candidate.id
      this.$network_utils
        .createBlockedDossier({ id: candidate.id })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'הפירוט נוצר ברקע - יופיע כאן בעוד כמה דקות' })
          this.loadCandidates()
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'יצירת הפירוט נכשלה', type: 'error' })
        })
        .finally(() => {
          this.busy_id = null
        })
    },
    formatDate (value) {
      if (!value) return '-'
      const d = new Date(value)
      if (isNaN(d.getTime())) return value
      return d.toLocaleDateString('he-IL') + ' ' + d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.blocked-candidates-page {
  padding: 20px;
  text-align: right;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #2c3e50;
}
.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 18px;
  line-height: 1.6;
}
.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.include-released {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #475569;
  margin-bottom: 12px;
  cursor: pointer;
}
.table-wrap {
  overflow-x: auto;
}
.col-reason {
  max-width: 240px;
  white-space: pre-wrap;
  word-break: break-word;
}
.is-released td {
  color: #94a3b8;
}
.released-note {
  font-size: 12px;
  color: #64748b;
}
.cleanup-error {
  font-size: 12px;
  color: #c0392b;
  margin-top: 2px;
  word-break: break-word;
}
.unblock-btn:disabled,
.details-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.blocked-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.blocked-table th,
.blocked-table td {
  padding: 10px 12px;
  font-size: 14px;
  text-align: right;
}
.blocked-table tr {
  border-bottom: 2px solid #cbd5e0;
}
.blocked-table th {
  background: #f1f5f9;
  font-weight: 700;
  color: #2c3e50;
}
.ltr-cell {
  direction: ltr;
  text-align: right;
}
.col-num {
  width: 120px;
  text-align: center;
}
.col-action {
  min-width: 220px;
  white-space: nowrap;
}
.details-btn {
  background: #fff;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
  margin-left: 8px;
}
.details-btn:hover:not(:disabled) {
  background: #f1f5f9;
}
.details-btn.is-muted {
  color: #a3adba;
  border-color: #e2e8f0;
  background: #f8fafc;
  cursor: default;
}
.unblock-btn {
  background: #fff;
  border: 1px solid #e0b4b0;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  cursor: pointer;
}
.unblock-btn:hover {
  background: #fdecea;
}
.no-results {
  padding: 30px;
  text-align: center;
  color: #7f8c8d;
}
.details-overlay {
  position: fixed;
  inset: 0;
  background: rgba(44, 62, 80, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}
.details-dialog {
  width: 720px;
  max-width: 92vw;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.3);
  padding: 18px 20px 16px;
  text-align: right;
}
.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 10px;
  margin-bottom: 12px;
}
.details-title {
  font-size: 17px;
  font-weight: 700;
  color: #2c3e50;
}
.details-close-x {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.details-body {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 60vh;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.7;
  color: #334155;
}
.details-note {
  margin-top: 10px;
  font-size: 12px;
  color: #94a3b8;
}
.details-footer {
  display: flex;
  justify-content: flex-start;
  margin-top: 14px;
}
.details-close-btn {
  background: #fff;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 6px 22px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
}
.details-close-btn:hover {
  background: #f1f5f9;
}
</style>
